import { isEmptyString } from '@/utils/string';

/**
 * The two spellings of an event's attributes.
 *
 * The modal offers a key/value grid and a JSON editor over the same value, so
 * whichever one is on screen has to be able to read what the other wrote.
 * JSON is the one that is sent, and the grid is a view of it: a key whose
 * value is itself JSON round-trips, and a value that is not is left as the
 * string it was typed as.
 */

/**
 * One row of the grid. A value read out of JSON keeps the type it had, so a
 * number that nobody edits is still sent as a number; a value that has been
 * typed is a string until it parses as something else.
 */
export type EventDataField = {
  key: string;
  value: string | number;
  /**
   * What the value was when the grid read it, for a value the grid cannot
   * edit as itself — a boolean, a null, a list, an object. Kept so a row
   * nobody touched is sent as it arrived rather than as its own text, and
   * dropped the moment the row is edited.
   */
  asRead?: JsonValue;
};

/** A grid always offers one empty row, so there is somewhere to type. */
export const EMPTY_EVENT_DATA_FIELD: EventDataField = { key: '', value: '' };

/**
 * What an event attribute can hold. The endpoint takes whatever JSON the
 * workspace sends, so the shape is the JSON grammar itself rather than
 * anything this app defines.
 */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type EventAttributes = Record<string, JsonValue>;

const JSON_CONTAINER = /^\s*[[{]/;

function parseObject(data: string): EventAttributes {
  try {
    const parsed: JsonValue = JSON.parse(data);
    // A list or a bare value is valid JSON but describes no attributes, so
    // the grid has nothing to show for it.
    if (typeof parsed !== 'object' || parsed == null || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

/** A row's value, read as JSON when it looks like an object or a list. */
function parseFieldValue(value: string | number): JsonValue {
  if (typeof value !== 'string' || !JSON_CONTAINER.test(value)) {
    return value;
  }
  try {
    const parsed: JsonValue = JSON.parse(value);
    return parsed;
  } catch {
    return value;
  }
}

/** The JSON the grid describes. Rows with no key are not attributes yet. */
export function fieldsToJson(fields: readonly EventDataField[]): string {
  // Built from entries rather than by assignment: an attribute may be called
  // `__proto__`, and assigning that on an object sets its prototype instead
  // of adding the attribute, which drops it from what is sent.
  const attributes: EventAttributes = Object.fromEntries(
    fields
      .filter((field) => !isEmptyString(field.key))
      .map((field) => [field.key.trim(), field.asRead === undefined ? parseFieldValue(field.value) : field.asRead]),
  );

  return JSON.stringify(attributes, null, 2);
}

/** The grid that describes a piece of JSON, with one empty row when it describes nothing. */
export function jsonToFields(data: string): EventDataField[] {
  const fields = Object.entries(parseObject(data)).map(([key, value]) => {
    // A string or a number is what the grid edits; anything else is shown as
    // the JSON it is, and is remembered so an untouched row keeps its type.
    if (typeof value === 'string' || typeof value === 'number') {
      return { key, value };
    }
    return { key, value: JSON.stringify(value), asRead: value };
  });

  return fields.length === 0 ? [{ ...EMPTY_EVENT_DATA_FIELD }] : fields;
}

/**
 * Whether a caller handed over a payload worth keeping.
 *
 * Wider than "has named attributes": a top-level
 * list or a bare value is still event data, it is still sent, and dropping it
 * because the grid cannot show it would lose a resend's recorded payload with
 * nothing on screen saying so.
 */
export function hasAnyEventData(data: string | undefined): boolean {
  if (data == null || isEmptyString(data)) {
    return false;
  }
  try {
    const parsed: JsonValue = JSON.parse(data);
    return parsed != null;
  } catch {
    // Not valid JSON. The editor will say so; keeping it lets the sender fix
    // it rather than wonder where it went.
    return true;
  }
}

/**
 * Whether the grid can show this payload without losing it.
 *
 * A top-level list or a bare value is valid event data and is sent as-is,
 * but it is not a set of named attributes, so there are no rows to put it
 * in. Empty data has nothing to lose and starts with one blank row.
 */
export function canShowAsRows(data: string): boolean {
  if (isEmptyString(data)) {
    return true;
  }
  try {
    const parsed: JsonValue = JSON.parse(data);
    return typeof parsed === 'object' && parsed != null && !Array.isArray(parsed);
  } catch {
    // Not parseable yet, which the JSON editor reports on its own; the grid
    // shows what it can.
    return true;
  }
}

/**
 * The attribute names in a piece of event data. Analytics counts them.
 *
 * A list has entries rather than named attributes, and they are counted:
 * Ember reads `Object.keys` of whatever parses, so counting nothing here
 * would put the same send in two different buckets.
 */
export function eventDataAttributeNames(data: string): string[] {
  try {
    const parsed: JsonValue = JSON.parse(data);
    return typeof parsed !== 'object' || parsed == null ? [] : Object.keys(parsed);
  } catch {
    return [];
  }
}

/** What the endpoint is sent: compact JSON, or an empty string for no attributes. */
export function toEventDataPayload(data: string): string {
  if (isEmptyString(data)) {
    return '';
  }
  try {
    return JSON.stringify(JSON.parse(data));
  } catch {
    return '';
  }
}
